import { ActionIcon, Box, Collapse, Grid, Paper, Title } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { DateTime } from "luxon";
import type { ProspectInDetails } from "@/models";
import { ChecklistItem } from "@/components/ChecklistItem";
import { DataDisplay } from "@/components/DataDisplay";
import { RoutedTabs } from "@/components/RoutedTabs";
import { getProspectByIdQuery, updateProspectChecklistField } from "@/api";
import { getUserClaimsQuery } from "@/api/userClaims/getUserClaims";
import { canModifyProspectChecklist } from "@/security/logic";
import { getNameForDisplay } from "@/utils/getNameForDisplay";
import LoadingPane from "@/components/LoadingPane";
import { showError, showSuccess } from "@/notifications/Notifications";

export const Route = createFileRoute("/prospects/$prospectId")({
  component: RouteComponent,
  beforeLoad: async ({ context, params }) => {
    const prospectId = Number(params.prospectId);
    const { secureAxios } = context.apiHook;
    const prospect = await context.queryClient.ensureQueryData(
      getProspectByIdQuery(secureAxios, prospectId)
    );

    return {
      prospect,
      prospectName: getNameForDisplay(prospect)
    };
  },
  loader: ({ context }) => {
    const { prospectName } = context;
    return {
      crumb: prospectName
    };
  }
});

function RouteComponent() {
  const { prospectId } = Route.useParams();
  const routeContext = Route.useRouteContext();
  const { secureAxios } = routeContext.apiHook;
  const { prospectName } = routeContext;
  const queryClient = useQueryClient();
  const [isSavingReadinessCheckmark, setIsSavingReadinessCheckmark] =
    useState(false);
  const [isInfoSectionExpanded, setIsInfoSectionExpanded] = useState(true);

  const { data: prospect } = useQuery(
    getProspectByIdQuery(secureAxios, Number(prospectId))
  );

  const { data: userClaims } = useQuery(getUserClaimsQuery(secureAxios));
  const isReadOnly =
    !prospect || !canModifyProspectChecklist(userClaims, prospect.communityId);

  const mutation = useMutation<
    boolean,
    Error,
    { fieldName: string; value: boolean }
  >({
    mutationFn: async ({ fieldName, value }) => {
      return await updateProspectChecklistField(
        secureAxios,
        prospect?.id ?? 0,
        fieldName,
        value
      );
    },
    onSuccess: (updatedValue, variables) => {
      queryClient.setQueryData<ProspectInDetails>(
        ["prospect", prospect?.id],
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          return {
            ...oldData,
            [variables.fieldName]: updatedValue
          };
        }
      );

      showSuccess("Checklist updated successfully", "Saved");
    },
    onError: (error) => {
      showError("Failed to update checklist. Please try again.");
      console.error("Failed to update checklist:", error);
    }
  });

  const handleChecklistToggle = async (
    fieldName: string,
    newValue: boolean
  ) => {
    setIsSavingReadinessCheckmark(true);
    try {
      await mutation.mutateAsync({
        fieldName,
        value: newValue
      });
    } finally {
      setIsSavingReadinessCheckmark(false);
    }
  };

  if (!prospect) {
    return <LoadingPane />;
  }

  const calculateAge = (dateOfBirth: string): number | null => {
    const birthDate = DateTime.fromISO(dateOfBirth);
    if (!birthDate.isValid) {
      return null;
    }
    const today = DateTime.now();
    return Math.floor(today.diff(birthDate, "years").years);
  };

  const age = calculateAge(prospect.dateOfBirth);

  return (
    <div>
      <Title order={3}>{prospectName}</Title>

      <Paper
        shadow="xs"
        withBorder
        radius="md"
        bg="sparkYellow.10"
        bd="1px solid sparkYellow.11"
        pb={10}
        pl={20}
        pr={15}
        mt={10}
        mb={{ base: 20, lg: 40 }}
        pos="relative"
      >
        <ActionIcon
          variant="subtle"
          pos="absolute"
          size={20}
          top={20}
          left={0}
          onClick={() => setIsInfoSectionExpanded((v) => !v)}
          aria-label={isInfoSectionExpanded ? "Collapse" : "Expand"}
        >
          <IconChevronDown
            size={16}
            style={{
              transform: isInfoSectionExpanded
                ? "rotate(0deg)"
                : "rotate(180deg)",
              transition: "transform 200ms"
            }}
          />
        </ActionIcon>
        <Grid align="flex-start" gutter="xl">
          <Grid.Col span={5}>
            <Title order={5} mb="sm" c="black">
              Personal Information
            </Title>
            <Collapse in={isInfoSectionExpanded}>
              <Grid mb="xs" gutter="xs">
                <Grid.Col span={{ base: 6, md: 4 }} order={{ base: 1, md: 1 }}>
                  <DataDisplay label="FIRST NAME" value={prospect.firstName} />
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }} order={{ base: 2, md: 2 }}>
                  <DataDisplay label="LAST NAME" value={prospect.lastName} />
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }} order={{ base: 3, md: 3 }}>
                  <DataDisplay label="GENDER" value={prospect.gender} />
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }} order={{ base: 5, md: 4 }}>
                  <DataDisplay
                    label="DOB"
                    value={
                      prospect.dateOfBirth
                        ? DateTime.fromISO(prospect.dateOfBirth).toFormat(
                            "yyyy-MM-dd"
                          )
                        : undefined
                    }
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }} order={{ base: 6, md: 5 }}>
                  <DataDisplay label="AGE" value={age} />
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }} order={{ base: 4, md: 6 }}>
                  <DataDisplay
                    label="MARITAL STATUS"
                    value={prospect.maritalStatus}
                  />
                </Grid.Col>
              </Grid>
              <Grid gutter="xs">
                <Grid.Col span={prospect.email ? { base: 12, lg: 8 } : 4}>
                  <DataDisplay label="EMAIL" value={prospect.email} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <DataDisplay label="TELEPHONE" value={prospect.telephone} />
                </Grid.Col>
                <Grid.Col span={prospect.addressLine1 ? 12 : 4}>
                  <DataDisplay
                    label="ADDRESS"
                    value={`${prospect.addressLine1}\n${prospect.addressLine2 ? `${prospect.addressLine2}\n` : ""}${prospect.city} ${prospect.state}\n${prospect.zip}`}
                  />
                </Grid.Col>
              </Grid>
            </Collapse>
          </Grid.Col>
          <Grid.Col span={3}>
            <Title order={5} mb="sm" c="black">
              <Box component="span" visibleFrom="md">
                Onboarding Information
              </Box>
              <Box component="span" visibleFrom="sm" hiddenFrom="md">
                Onboarding Info
              </Box>
              <Box component="span" hiddenFrom="sm">
                Onboarding
              </Box>
            </Title>
            <Collapse in={isInfoSectionExpanded}>
              <Grid mb="xs" gutter="xs">
                <Grid.Col span={12}>
                  <DataDisplay
                    label="COMMUNITY"
                    value={prospect.communityName}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <DataDisplay label="STAGE" value={prospect.stageName} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <DataDisplay label="STATUS" value={prospect.status} />
                </Grid.Col>
              </Grid>
            </Collapse>
          </Grid.Col>
          <Grid.Col span={4}>
            <Title order={5} mb="sm" c="black">
              <Box component="span" visibleFrom="md">
                Neighborhood Readiness Checklist
              </Box>
              <Box component="span" visibleFrom="sm" hiddenFrom="md">
                Readiness Checklist
              </Box>
              <Box component="span" hiddenFrom="sm">
                Readiness
              </Box>
            </Title>
            <Collapse in={isInfoSectionExpanded}>
              <Grid gutter="xs">
                <Grid.Col span={{ base: 12, lg: 6 }}>
                  <ChecklistItem
                    label="DoC Interview"
                    checked={prospect.docInterviewComplete}
                    fieldName="docInterviewComplete"
                    onToggle={handleChecklistToggle}
                    disabled={isSavingReadinessCheckmark || isReadOnly}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 6 }}>
                  <ChecklistItem
                    label="Decision Made"
                    checked={prospect.decisionMade}
                    fieldName="decisionMade"
                    onToggle={handleChecklistToggle}
                    disabled={isSavingReadinessCheckmark || isReadOnly}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 6 }}>
                  <ChecklistItem
                    label="Tea and Chat"
                    checked={prospect.teaAndChatComplete}
                    fieldName="teaAndChatComplete"
                    onToggle={handleChecklistToggle}
                    disabled={isSavingReadinessCheckmark || isReadOnly}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 6 }}>
                  <ChecklistItem
                    label="Moved In"
                    checked={prospect.movedIn}
                    fieldName="movedIn"
                    onToggle={handleChecklistToggle}
                    disabled={isSavingReadinessCheckmark || isReadOnly}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 6 }}>
                  <ChecklistItem
                    label="Final Follow up"
                    checked={prospect.finalFollowUpComplete}
                    fieldName="finalFollowUpComplete"
                    onToggle={handleChecklistToggle}
                    disabled={isSavingReadinessCheckmark || isReadOnly}
                  />
                </Grid.Col>
              </Grid>
            </Collapse>
          </Grid.Col>
        </Grid>
      </Paper>
      <RoutedTabs
        route={Route}
        tabs={[
          { value: "recordings", label: "Recordings" },
          { value: "documents", label: "Documents" },
          { value: "living-story", label: "Living Story" }
        ]}
      />
      <Outlet />
    </div>
  );
}
